import React, { useEffect, useState } from 'react'
import api from '../api'
import { ClipboardList, SearchCode, RefreshCw, Clock3 } from 'lucide-react'
import { Card, PageHeader, Button, Badge, Banner, Alert, Tabs, EmptyState } from '../components/ui'

export default function SyncLog() {
  const [logs, setLogs] = useState([])
  const [status, setStatus] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [pesan, setPesan] = useState(null)
  const [logRaw, setLogRaw] = useState([])
  const [tab, setTab] = useState('sync')

  useEffect(() => { load() }, [])

  async function load() {
    const [l, s] = await Promise.all([
      api.get('/sync/log'),
      api.get('/sync/status')
    ])
    setLogs(l.data)
    setStatus(s.data)
  }

  async function loadRaw() {
    const res = await api.get('/sync/log-raw')
    setLogRaw(res.data)
  }

  useEffect(() => { if (tab === 'raw') loadRaw() }, [tab])

  async function syncManual() {
    setSyncing(true); setPesan(null)
    try {
      const res = await api.post('/sync/manual')
      setPesan({ type: 'success', text: res.data.message })
      setTimeout(() => load(), 3000)
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal memulai sync' })
    } finally { setSyncing(false) }
  }

  const statusBot = status?.connected

  return (
    <div>
      <PageHeader
        title="Sync & Log"
        subtitle="Riwayat sinkronisasi WhatsApp"
        actions={<>
          <Badge tone={statusBot ? 'success' : 'danger'}>{statusBot ? 'Bot Terhubung' : 'Bot Terputus'}</Badge>
          <Button icon={RefreshCw} loading={syncing} disabled={!statusBot} onClick={syncManual}>
            {syncing ? 'Sync berjalan...' : 'Sync Sekarang'}
          </Button>
        </>}
      />

      {pesan && <Alert type={pesan.type}>{pesan.text}</Alert>}

      <Banner tone="brand"><Clock3 size={15} style={{ flexShrink: 0 }} /> Sync otomatis berjalan setiap hari pukul <strong>&nbsp;23:00 WIB</strong></Banner>

      <Tabs
        tabs={[['sync', 'Riwayat Sync', ClipboardList], ['raw', 'Log Raw', SearchCode]]}
        active={tab} onChange={setTab}
      />

      {/* Tabel riwayat sync */}
      {tab === 'sync' && (
        <Card style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafc' }}>
                {['Waktu', 'Trigger', 'Pesan', 'Valid', 'Error', 'Skip', 'Foto', 'Durasi', 'Status'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={9}><EmptyState title="Belum ada riwayat sync" /></td></tr>
              ) : logs.map(l => (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{l.sync_time}</td>
                  <td style={{ padding: '10px 16px' }}><Badge tone={l.trigger === 'auto' ? 'brand' : 'warning'}>{l.trigger}</Badge></td>
                  <td style={{ padding: '10px 16px' }}>{l.total_pesan}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--success)', fontWeight: 600 }}>{l.total_valid}</td>
                  <td style={{ padding: '10px 16px', color: l.total_error > 0 ? 'var(--danger)' : 'var(--text-faint)' }}>{l.total_error}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-faint)' }}>{l.total_skip}</td>
                  <td style={{ padding: '10px 16px' }}>{l.total_foto}</td>
                  <td style={{ padding: '10px 16px' }}>{l.duration_ms}ms</td>
                  <td style={{ padding: '10px 16px' }}>
                    <Badge tone={l.status === 'success' ? 'success' : l.status === 'running' ? 'warning' : 'danger'}>{l.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Log raw */}
      {tab === 'raw' && (
        <Card style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafafc' }}>
                {['Waktu WA', 'Pengirim', 'Valid', 'Nama', 'Pekerjaan', 'Status', 'Error', 'Diproses'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logRaw.length === 0 ? (
                <tr><td colSpan={8}><EmptyState title="Belum ada log raw" /></td></tr>
              ) : logRaw.map(l => (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{l.timestamp_wa}</td>
                  <td style={{ padding: '9px 12px' }}>{l.no_wa_pengirim?.replace('@s.whatsapp.net', '') || '-'}</td>
                  <td style={{ padding: '9px 12px' }}><Badge tone={l.is_valid ? 'success' : 'danger'}>{l.is_valid ? 'Ya' : 'Tidak'}</Badge></td>
                  <td style={{ padding: '9px 12px' }}>{l.parsed_nama ? JSON.parse(l.parsed_nama).join(', ') : '-'}</td>
                  <td style={{ padding: '9px 12px' }}>{l.parsed_pekerjaan || '-'}</td>
                  <td style={{ padding: '9px 12px' }}>{l.parsed_status || '-'}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--danger)', fontSize: 11 }}>{l.error_message || '-'}</td>
                  <td style={{ padding: '9px 12px' }}><Badge tone={l.is_processed ? 'success' : 'neutral'}>{l.is_processed ? 'Ya' : 'Belum'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}
